import { apiFetch } from './apiClient';

/** @typedef {import('./dto/customer').CustomerDTO} CustomerDTO */
/** @typedef {import('./dto/article').ArticleDTO} ArticleDTO */
/** @typedef {import('./dto/shippingCost').ShippingCostDTO} ShippingCostDTO */
/** @typedef {import('./dto/supplier').SupplierDTO} SupplierDTO */
/** @typedef {import('./dto/employee').EmployeeDTO} EmployeeDTO */
/** @typedef {import('./dto/employee').PayrollImportRowDTO} PayrollImportRowDTO */
/** @typedef {import('./dto/employee').PayrollImportResultDTO} PayrollImportResultDTO */
/** @typedef {import('./dto/employee').PayrollStatementDTO} PayrollStatementDTO */
/** @typedef {import('./dto/legacy').LegacyImportResultDTO} LegacyImportResultDTO */
/** @typedef {import('./dto/bankCsv').BankCsvImportPreviewDTO} BankCsvImportPreviewDTO */
/** @typedef {import('./dto/bankCsv').BankCsvImportResultDTO} BankCsvImportResultDTO */
/** @typedef {import('./dto/paypal').PayPalImportPreviewDTO} PayPalImportPreviewDTO */
/** @typedef {import('./dto/paypal').PayPalImportResultDTO} PayPalImportResultDTO */
/** @typedef {import('./dto/paymentProvider').PaymentProviderTransactionDTO} PaymentProviderTransactionDTO */
/** @typedef {import('./dto/paymentProvider').PaymentProviderTransactionAssignmentDTO} PaymentProviderTransactionAssignmentDTO */
/** @typedef {import('./dto/workOrder').WorkOrderDTO} WorkOrderDTO */

const extractDispositionFilename = (headers) => {
    const disposition = headers.get('content-disposition') || headers.get('Content-Disposition');
    if (!disposition) {
        return null;
    }
    const match = /filename\*?=(?:UTF-8''|"?)([^";]+)/i.exec(disposition);
    if (!match || !match[1]) {
        return null;
    }
    const rawName = match[1].replace(/"/g, '').trim();
    try {
        return decodeURIComponent(rawName);
    } catch {
        return rawName;
    }
};

// Auth
export const login = async (data) => {
    const res = await apiFetch('/api/user/login', { method: 'POST', body: JSON.stringify(data) });
    const rawBody = await res.text();
    let payload = null;
    if (rawBody) {
        try {
            payload = JSON.parse(rawBody);
        } catch {
            payload = rawBody;
        }
    }

    return { status: res.status, data: payload };
};

export const register = async (data) => {
    const res = await apiFetch('/api/user/register', { method: 'POST', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const getCurrentUser = async () => {
    const res = await apiFetch('/api/user/me');
    return { status: res.status, data: await res.json() };
};

export const cancelSubscription = async (data) => {
    const res = await apiFetch('/api/user/cancel-subscription', { method: 'POST', body: JSON.stringify(data) });
    const text = await res.text();
    return { status: res.status, data: text };
};

export const refreshTokens = async (refreshToken) => {
    const res = await apiFetch('/api/user/token/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
        skipAuthRefresh: true,
    });
    return { status: res.status, data: await res.json() };
};

export const changePassword = async (data) => {
    const res = await apiFetch('/api/user/change-password', { method: 'POST', body: JSON.stringify(data) });
    const text = await res.text();
    return { status: res.status, data: text };
};

export const getSubscriptionPlans = async () => {
    const res = await apiFetch('/api/subscription/plans');
    return { status: res.status, data: await res.json() };
};

export const changeSubscriptionPlan = async (data) => {
    const res = await apiFetch('/api/subscription/change', { method: 'POST', body: JSON.stringify(data) });
    const rawBody = await res.text();
    let payload = null;
    if (rawBody) {
        try {
            payload = JSON.parse(rawBody);
        } catch {
            payload = rawBody;
        }
    }

    return { status: res.status, data: payload };
};

// Admin / Roles
export const getUsers = async () => {
    const res = await apiFetch('/api/user');
    return { status: res.status, data: await res.json() };
};

export const updateUserRoles = async (id, payload) => {
    const res = await apiFetch(`/api/user/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
    return { status: res.status, data: await res.json() };
};

export const resetUserPassword = async (id) => {
    const res = await apiFetch(`/api/user/${id}/reset-password`, { method: 'POST' });
    const text = await res.text();
    return { status: res.status, data: text };
};

export const getRoles = async () => {
    const res = await apiFetch('/api/admin/roles');
    return { status: res.status, data: await res.json() };
};

export const createRole = async (data) => {
    const res = await apiFetch('/api/admin/roles', { method: 'POST', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const updateRole = async (id, data) => {
    const res = await apiFetch(`/api/admin/roles/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const deleteRole = async (id) => {
    const res = await apiFetch(`/api/admin/roles/${id}`, { method: 'DELETE' });
    return { status: res.status, data: await res.text() };
};

export const getPermissionsList = async () => {
    const res = await apiFetch('/api/admin/permissions');
    return { status: res.status, data: await res.json() };
};

// Notifications
export const getNotificationStatus = async () => {
    const res = await apiFetch('/api/notifications/status');
    return { status: res.status, data: await res.json() };
};

export const updateNotificationOptIn = async (payload) => {
    const res = await apiFetch('/api/notifications/opt-in', { method: 'POST', body: JSON.stringify(payload) });
    return { status: res.status, data: await res.json() };
};

/** @param {import('./dto/issue').IssueReportRequest} payload */
export const submitIssueReport = async (payload) => {
    const res = await apiFetch('/api/issue-reports', { method: 'POST', body: JSON.stringify(payload) });
    const rawBody = await res.text();
    let data = null;
    if (rawBody) {
        try {
            data = JSON.parse(rawBody);
        } catch {
            data = rawBody;
        }
    }
    return { status: res.status, data };
};

/** @param {import('./dto/suggestion').SuggestionReportRequest} payload */
export const submitImprovementSuggestion = async (payload) => {
    const res = await apiFetch('/api/improvement-suggestions', { method: 'POST', body: JSON.stringify(payload) });
    const rawBody = await res.text();
    let data = null;
    if (rawBody) {
        try {
            data = JSON.parse(rawBody);
        } catch {
            data = rawBody;
        }
    }
    return { status: res.status, data };
};

export const searchFaq = async (query) => {
    const url = `/api/faq/search${query ? `?query=${encodeURIComponent(query)}` : ''}`;
    const res = await apiFetch(url);
    return { status: res.status, data: await res.json() };
};

export const getReleaseNotes = async () => {
    const res = await apiFetch('/api/release-notes');
    return { status: res.status, data: await res.json() };
};

// Work Orders
/** @returns {Promise<{status:number,data:WorkOrderDTO[]}>} */
export const getWorkOrders = async (options = {}) => {
    const res = await apiFetch('/api/orders', { suppressAuthError: options.suppressAuthError });
    return { status: res.status, data: await res.json() };
};

/** @param {Partial<WorkOrderDTO>} data */
export const saveWorkOrder = async (data) => {
    const res = await apiFetch('/api/orders', { method: 'POST', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const deleteWorkOrder = async (id) => {
    const res = await apiFetch(`/api/orders/${id}`, { method: 'DELETE' });
    return { status: res.status, data: await res.text() };
};

// Customers
/** @returns {Promise<{status:number,data:CustomerDTO[]}>} */
export const getCustomers = async (options = {}) => {
    const pageSize = 200;
    let page = 0;
    let totalPages = null;
    let status = 200;
    const customers = [];

    while (totalPages === null || page < totalPages) {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('size', String(pageSize));
        const res = await apiFetch(`/api/customer?${params.toString()}`, {
            suppressAuthError: options.suppressAuthError,
        });
        status = res.status;
        const payload = await res.json();

        const pageItems = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.content)
                ? payload.content
                : [];
        customers.push(...pageItems);

        if (Array.isArray(payload)) {
            break;
        }

        if (typeof payload?.totalPages === 'number') {
            totalPages = payload.totalPages;
        } else if (payload?.last === true || pageItems.length < pageSize) {
            break;
        } else {
            totalPages = page + 2;
        }

        page += 1;
    }

    return { status, data: customers };
};

/** @param {CustomerDTO} data */
export const createCustomer = async (data) => {
    const res = await apiFetch('/api/customer', { method: 'POST', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

/** @param {string} id @param {CustomerDTO} data */
export const updateCustomer = async (id, data) => {
    const res = await apiFetch(`/api/customer/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const deleteCustomer = async (id) => {
    const res = await apiFetch(`/api/customer/${id}`, { method: 'DELETE' });
    return { status: res.status, data: await res.text() };
};

// Suppliers
/** @returns {Promise<{status:number,data:SupplierDTO[]}>} */
export const getSuppliers = async () => {
    const res = await apiFetch('/api/supplier');
    return { status: res.status, data: await res.json() };
};

/** @param {SupplierDTO} data */
export const createSupplier = async (data) => {
    const res = await apiFetch('/api/supplier', { method: 'POST', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

/** @param {string} id @param {SupplierDTO} data */
export const updateSupplier = async (id, data) => {
    const res = await apiFetch(`/api/supplier/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const deleteSupplier = async (id) => {
    const res = await apiFetch(`/api/supplier/${id}`, { method: 'DELETE' });
    return { status: res.status, data: await res.text() };
};

// Employees
/** @returns {Promise<{status:number,data:EmployeeDTO[]}>} */
export const getEmployees = async () => {
    const res = await apiFetch('/api/employee');
    return { status: res.status, data: await res.json() };
};

/** @param {EmployeeDTO} data */
export const createEmployee = async (data) => {
    const res = await apiFetch('/api/employee', { method: 'POST', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

/** @param {string} id @param {EmployeeDTO} data */
export const updateEmployee = async (id, data) => {
    const res = await apiFetch(`/api/employee/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const deleteEmployee = async (id) => {
    const res = await apiFetch(`/api/employee/${id}`, { method: 'DELETE' });
    return { status: res.status, data: await res.text() };
};

/** @returns {Promise<{status:number,data:PayrollStatementDTO[]}>} */
export const getEmployeeStatements = async (id) => {
    const res = await apiFetch(`/api/employee/${id}/statements`);
    return { status: res.status, data: await res.json() };
};

/** @returns {Promise<{status:number,data:any}>} */
export const getCostCenters = async () => {
    const res = await apiFetch('/api/cost-centers');
    return { status: res.status, data: await res.json() };
};

/** @returns {Promise<{status:number,data:PayrollImportRowDTO[]}>} */
export const previewPayrollImport = async (formData) => {
    const res = await apiFetch('/api/payroll/import/preview', { method: 'POST', body: formData });
    return { status: res.status, data: await res.json() };
};

/** @returns {Promise<{status:number,data:PayrollImportResultDTO}>} */
export const importPayroll = async (payload) => {
    const res = await apiFetch('/api/payroll/import', { method: 'POST', body: JSON.stringify(payload) });
    return { status: res.status, data: await res.json() };
};

/** @returns {Promise<{status:number,data:LegacyImportResultDTO|string}>} */
export const importLegacyDatabase = async (formData) => {
    const res = await apiFetch('/api/import/legacy', {
        method: 'POST',
        body: formData,
        timeoutMs: 120000,
    });
    const rawBody = await res.text();
    let payload = null;
    if (rawBody) {
        try {
            payload = JSON.parse(rawBody);
        } catch {
            payload = rawBody;
        }
    }
    return { status: res.status, data: payload };
};

/** @returns {Promise<{status:number,data:import('./dto/legacy').LegacyImportJobCreatedDTO|string}>} */
export const startLegacyImportJob = async (formData) => {
    const res = await apiFetch('/api/import/legacy/jobs', {
        method: 'POST',
        body: formData,
        timeoutMs: 30000,
    });
    const rawBody = await res.text();
    let payload = null;
    if (rawBody) {
        try {
            payload = JSON.parse(rawBody);
        } catch {
            payload = rawBody;
        }
    }
    return { status: res.status, data: payload };
};

/** @returns {Promise<{status:number,data:import('./dto/legacy').LegacyImportJobStatusDTO|string}>} */
export const getLegacyImportJob = async (jobId) => {
    const res = await apiFetch(`/api/import/legacy/jobs/${jobId}`, {
        method: 'GET',
        timeoutMs: 30000,
    });
    const rawBody = await res.text();
    let payload = null;
    if (rawBody) {
        try {
            payload = JSON.parse(rawBody);
        } catch {
            payload = rawBody;
        }
    }
    return { status: res.status, data: payload };
};

/** @returns {Promise<{status:number,data:import('./dto/legacy').LegacyImportPreviewSummaryDTO|string}>} */
export const previewLegacySummary = async (formData) => {
    const res = await apiFetch('/api/import/legacy/preview/summary', { method: 'POST', body: formData });
    const rawBody = await res.text();
    let payload = null;
    if (rawBody) {
        try {
            payload = JSON.parse(rawBody);
        } catch {
            payload = rawBody;
        }
    }
    return { status: res.status, data: payload };
};

/** @returns {Promise<{status:number,data:any[]|string}>} */
export const previewLegacySection = async (formData, type, offset = 0, limit = 100) => {
    const params = new URLSearchParams({
        type: String(type),
        offset: String(offset),
        limit: String(limit),
    });
    const res = await apiFetch(`/api/import/legacy/preview/section?${params.toString()}`, {
        method: 'POST',
        body: formData,
    });
    const rawBody = await res.text();
    let payload = null;
    if (rawBody) {
        try {
            payload = JSON.parse(rawBody);
        } catch {
            payload = rawBody;
        }
    }
    return { status: res.status, data: payload };
};

/** @returns {Promise<{status:number,data:import('./dto/legacy').LegacyYearPreviewDTO[]|string}>} */
export const previewLegacySectionYears = async (formData, type) => {
    const params = new URLSearchParams({
        type: String(type),
    });
    const res = await apiFetch(`/api/import/legacy/preview/section/years?${params.toString()}`, {
        method: 'POST',
        body: formData,
    });
    const rawBody = await res.text();
    let payload = null;
    if (rawBody) {
        try {
            payload = JSON.parse(rawBody);
        } catch {
            payload = rawBody;
        }
    }
    return { status: res.status, data: payload };
};

/** @returns {Promise<{status:number,data:any[]|string}>} */
export const previewLegacySectionByYear = async (formData, type, year, offset = 0, limit = 100) => {
    const params = new URLSearchParams({
        type: String(type),
        year: String(year),
        offset: String(offset),
        limit: String(limit),
    });
    const res = await apiFetch(`/api/import/legacy/preview/section/year?${params.toString()}`, {
        method: 'POST',
        body: formData,
    });
    const rawBody = await res.text();
    let payload = null;
    if (rawBody) {
        try {
            payload = JSON.parse(rawBody);
        } catch {
            payload = rawBody;
        }
    }
    return { status: res.status, data: payload };
};

/** @returns {Promise<{status:number,data:import('./dto/legacy').LegacyImportPreviewDTO|string}>} */
export const previewLegacyDatabase = async (formData) => {
    const res = await apiFetch('/api/import/legacy/preview', { method: 'POST', body: formData });
    const rawBody = await res.text();
    let payload = null;
    if (rawBody) {
        try {
            payload = JSON.parse(rawBody);
        } catch {
            payload = rawBody;
        }
    }
    return { status: res.status, data: payload };
};

/** @returns {Promise<{status:number,data:BankCsvImportPreviewDTO|string}>} */
export const previewBankCsvImport = async (formData) => {
    const res = await apiFetch('/api/import/bank-csv/preview', { method: 'POST', body: formData });
    const rawBody = await res.text();
    let payload = null;
    if (rawBody) {
        try {
            payload = JSON.parse(rawBody);
        } catch {
            payload = rawBody;
        }
    }
    return { status: res.status, data: payload };
};

/** @returns {Promise<{status:number,data:BankCsvImportResultDTO|string}>} */
export const importBankCsvTransactions = async (formData) => {
    const res = await apiFetch('/api/import/bank-csv', { method: 'POST', body: formData });
    const rawBody = await res.text();
    let payload = null;
    if (rawBody) {
        try {
            payload = JSON.parse(rawBody);
        } catch {
            payload = rawBody;
        }
    }
    return { status: res.status, data: payload };
};

/** @returns {Promise<{status:number,data:any[]}>} */
export const getPendingBankTransactions = async () => {
    const res = await apiFetch('/api/banking/review');
    return { status: res.status, data: await res.json() };
};

/** @returns {Promise<{status:number,data:any}>} */
export const acceptBankTransaction = async (id, payload) => {
    const res = await apiFetch(`/api/banking/${id}/accept`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return { status: res.status, data: await res.json() };
};

/** @returns {Promise<{status:number,data:any}>} */
export const allocateBankTransaction = async (id, payload) => {
    const res = await apiFetch(`/api/banking/${id}/allocations`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return { status: res.status, data: await res.json() };
};

/** @returns {Promise<{status:number,data:string}>} */
export const deleteBankTransaction = async (id) => {
    const res = await apiFetch(`/api/banking/${id}`, { method: 'DELETE' });
    return { status: res.status, data: await res.text() };
};

/** @returns {Promise<{status:number,data:any[]}>} */
export const getAssignedBankTransactions = async () => {
    const res = await apiFetch('/api/banking/assigned');
    return { status: res.status, data: await res.json() };
};

/** @returns {Promise<{status:number,data:PayPalImportPreviewDTO|string}>} */
export const previewPayPalImport = async (formData) => {
    const res = await apiFetch('/api/import/paypal/preview', { method: 'POST', body: formData });
    const rawBody = await res.text();
    let payload = null;
    if (rawBody) {
        try {
            payload = JSON.parse(rawBody);
        } catch {
            payload = rawBody;
        }
    }
    return { status: res.status, data: payload };
};

/** @returns {Promise<{status:number,data:PayPalImportResultDTO|string}>} */
export const importPayPalTransactions = async (formData) => {
    const res = await apiFetch('/api/import/paypal', { method: 'POST', body: formData });
    const rawBody = await res.text();
    let payload = null;
    if (rawBody) {
        try {
            payload = JSON.parse(rawBody);
        } catch {
            payload = rawBody;
        }
    }
    return { status: res.status, data: payload };
};

/** @returns {Promise<{status:number,data:PaymentProviderTransactionDTO[]}>} */
export const getUnassignedPaymentProviderTransactions = async () => {
    const res = await apiFetch('/api/payment-provider-transactions/unassigned');
    return { status: res.status, data: await res.json() };
};

/** @returns {Promise<{status:number,data:PaymentProviderTransactionDTO[]}>} */
export const getAssignedPaymentProviderTransactions = async () => {
    const res = await apiFetch('/api/payment-provider-transactions/assigned');
    return { status: res.status, data: await res.json() };
};

/**
 * @param {string} transactionId
 * @param {PaymentProviderTransactionAssignmentDTO} payload
 * @returns {Promise<{status:number,data:PaymentProviderTransactionDTO}>}
 */
export const allocatePaymentProviderTransaction = async (transactionId, payload) => {
    const res = await apiFetch(`/api/payment-provider-transactions/${transactionId}/allocations`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return { status: res.status, data: await res.json() };
};

/** @returns {Promise<{status:number,data:string}>} */
export const deletePaymentProviderTransaction = async (transactionId) => {
    const res = await apiFetch(`/api/payment-provider-transactions/${transactionId}`, {
        method: 'DELETE',
    });
    return { status: res.status, data: await res.text() };
};

// Articles
/** @returns {Promise<{status:number,data:ArticleDTO[]}>} */
export const getArticles = async (options = {}) => {
    const res = await apiFetch('/api/article', { suppressAuthError: options.suppressAuthError });
    return { status: res.status, data: await res.json() };
};

export const getArticleNextNumber = async () => {
    const res = await apiFetch('/api/article/next-number');
    const json = await res.json();
    return { status: res.status, data: json.nextNumber };
};

export const validateArticleNumber = async (number) => {
    const res = await apiFetch(`/api/article/validate-number?number=${encodeURIComponent(number)}`);
    // HIER: korrekt JSON lesen (vorher res.data)
    return { status: res.status, data: await res.json() };
};

/** @param {string} id @param {ArticleDTO} data */
export const updateArticle = async (id, data) => {
    const res = await apiFetch(`/api/article/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

/** @param {ArticleDTO} data */
export const createArticle = async (data) => {
    const res = await apiFetch('/api/article', { method: 'POST', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const deleteArticle = async (id) => {
    const res = await apiFetch(`/api/article/${id}`, { method: 'DELETE' });
    return { status: res.status, data: await res.text() };
};

// Shipping costs
/** @returns {Promise<{status:number,data:ShippingCostDTO[]}>} */
export const getShippingCosts = async (options = {}) => {
    const res = await apiFetch('/api/shipping-cost', { suppressAuthError: options.suppressAuthError });
    return { status: res.status, data: await res.json() };
};

/** @param {string} id @param {ShippingCostDTO} data */
export const updateShippingCost = async (id, data) => {
    const res = await apiFetch(`/api/shipping-cost/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

/** @param {ShippingCostDTO} data */
export const createShippingCost = async (data) => {
    const res = await apiFetch('/api/shipping-cost', { method: 'POST', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const deleteShippingCost = async (id) => {
    const res = await apiFetch(`/api/shipping-cost/${id}`, { method: 'DELETE' });
    const payload = await res.text();
    return { status: res.status, data: payload };
};

// Profile
export const getProfil = async (options = {}) => {
    const res = await apiFetch('/api/profil', { suppressAuthError: options.suppressAuthError });
    return { status: res.status, data: await res.json() };
};

export const getProfilLogos = async () => {
    const res = await apiFetch('/api/profil/logos');
    return { status: res.status, data: await res.json() };
};

export const getProfilLogo = async () => {
    const res = await apiFetch('/api/profil/logo');
    return { status: res.status, data: await res.blob() };
};

export const getProfilLogoById = async (id) => {
    const res = await apiFetch(`/api/profil/logo/${id}`);
    return { status: res.status, data: await res.blob(), contentType: res.headers.get('Content-Type') };
};

export const setProfil = async (data) => {
    const res = await apiFetch('/api/setProfil', { method: 'PUT', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const uploadProfilLogo = async (formData) => {
    const res = await apiFetch('/api/profil/upload-logo', { method: 'POST', body: formData });
    return { status: res.status, data: await res.json() };
};

export const deleteProfilLogo = async (id) => {
    const res = await apiFetch(`/api/profil/logo/${id}`, { method: 'DELETE' });
    const payload = await res.text();
    return { status: res.status, data: payload };
};

export const setProfilPrimaryLogo = async (id) => {
    const res = await apiFetch(`/api/profil/logo/${id}/primary`, { method: 'POST' });
    return { status: res.status, data: await res.json() };
};

export const getSettings = async () => {
    const res = await apiFetch('/api/settings');
    return { status: res.status, data: await res.json() };
};

export const updateSettings = async (data) => {
    const res = await apiFetch('/api/settings', { method: 'PUT', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

// Invoices
export const getInvoices = async () => {
    const res = await apiFetch('/api/invoice');
    return { status: res.status, data: await res.json() };
};

export const getOffers = async (options = {}) => {
    const res = await apiFetch('/api/invoice/offers', { suppressAuthError: options.suppressAuthError });
    return { status: res.status, data: await res.json() };
};

export const getInvoiceMonths = async (options = {}) => {
    const res = await apiFetch('/api/invoice/months', { suppressAuthError: options.suppressAuthError });
    return { status: res.status, data: await res.json() };
};

export const getInvoicesForMonth = async (year, monthIndex, options = {}) => {
    const params = new URLSearchParams();
    params.set('year', String(year));
    params.set('month', String(Number(monthIndex) + 1));
    const res = await apiFetch(`/api/invoice/month?${params.toString()}`, {
        suppressAuthError: options.suppressAuthError,
    });
    return { status: res.status, data: await res.json() };
};

export const getInvoicesSummary = async () => {
    const res = await apiFetch('/api/invoice/summary');
    return { status: res.status, data: await res.json() };
};

export const getOpenInvoices = async (params = {}) => {
    const search = new URLSearchParams();
    if (Array.isArray(params.buckets)) {
        params.buckets.filter(Boolean).forEach((bucket) => search.append('bucket', bucket));
    }
    if (params.level) {
        search.append('level', params.level);
    }
    if (params.dueFrom) {
        search.append('dueFrom', params.dueFrom);
    }
    if (params.dueTo) {
        search.append('dueTo', params.dueTo);
    }
    if (params.customer) {
        search.append('customer', params.customer);
    }
    const query = search.toString();
    const res = await apiFetch(`/api/invoice/open${query ? `?${query}` : ''}`);
    return { status: res.status, data: await res.json() };
};

export const createInvoice = async (payload) => {
    const res = await apiFetch('/api/invoice', { method: 'POST', body: JSON.stringify(payload) });
    return { status: res.status, data: await res.json() };
};

export const sendInvoiceEmail = async (id, options = {}) => {
    const res = await apiFetch(`/api/invoice/${id}/email`, {
        method: 'POST',
        suppressAuthError: options.suppressAuthError,
    });
    return { status: res.status, data: res.status === 204 ? null : await res.text() };
};

export const cancelInvoice = async (id) => {
    const res = await apiFetch(`/api/invoice/${id}/cancellation`, { method: 'POST' });
    return { status: res.status, data: await res.json() };
};

export const downloadInvoicePdf = async (id, options = {}) => {
    const res = await apiFetch(`/api/invoice/${id}/pdf`, {
        headers: { Accept: 'application/pdf' },
        suppressAuthError: options.suppressAuthError,
    });
    const blob = await res.blob();
    const disposition = res.headers.get('content-disposition');
    let fileName = null;
    if (disposition) {
        const match = /filename\*?=(?:UTF-8''|"?)([^";]+)/i.exec(disposition);
        if (match && match[1]) {
            const rawName = match[1].replace(/"/g, '').trim();
            try {
                fileName = decodeURIComponent(rawName);
            } catch {
                fileName = rawName;
            }
        }
    }
    return { status: res.status, data: blob, fileName };
};

export const downloadInvoiceXRechnung = async (id) => {
    const res = await apiFetch(`/api/invoice/${id}/xrechnung`, {
        headers: { Accept: 'application/xml' },
    });
    const blob = await res.blob();
    const disposition = res.headers.get('content-disposition');
    let fileName = null;
    if (disposition) {
        const match = /filename\*?=(?:UTF-8''|"?)([^";]+)/i.exec(disposition);
        if (match && match[1]) {
            const rawName = match[1].replace(/"/g, '').trim();
            try {
                fileName = decodeURIComponent(rawName);
            } catch {
                fileName = rawName;
            }
        }
    }
    return { status: res.status, data: blob, fileName };
};

export const getInvoiceNextNumber = async (year, invoiceType, options = {}) => {
    const params = new URLSearchParams({ year: String(year) });
    if (invoiceType) {
        params.set('invoiceType', invoiceType);
    }
    const res = await apiFetch(`/api/invoice/next-number?${params.toString()}`, {
        suppressAuthError: options.suppressAuthError,
    });
    // HIER: der Controller liefert String → text() (vorher res.data)
    return { status: res.status, data: await res.text() };
};

export const updateInvoicePaid = async (id, data) => {
    const res = await apiFetch(`/api/invoice/${id}/paid`, { method: 'PUT', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const updateInvoiceSent = async (id, data) => {
    const res = await apiFetch(`/api/invoice/${id}/sent`, { method: 'PUT', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const updateInvoiceWorkOrder = async (id, data) => {
    const res = await apiFetch(`/api/invoice/${id}/work-order`, { method: 'PUT', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const createInvoicePaymentLink = async (id) => {
    const res = await apiFetch(`/api/invoice/${id}/payment-link`, { method: 'POST' });
    return { status: res.status, data: await res.json() };
};

/** @param {import('./dto/invoice').InvoiceRequestDTO} payload */
export const previewInvoicePdf = async (payload) => {
    const res = await apiFetch('/api/invoice/preview-pdf', {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: { Accept: 'application/pdf' },
    });
    const blob = await res.blob();
    const disposition = res.headers.get('content-disposition');
    let fileName = null;
    if (disposition) {
        const match = /filename\*?=(?:UTF-8''|"?)([^";]+)/i.exec(disposition);
        if (match && match[1]) {
            const rawName = match[1].replace(/"/g, '').trim();
            try {
                fileName = decodeURIComponent(rawName);
            } catch {
                fileName = rawName;
            }
        }
    }
    return { status: res.status, data: blob, fileName };
};

// Reports
export const getCashflowForecast = async (scenario = 'baseline') => {
    const query = scenario ? `?scenario=${encodeURIComponent(scenario)}` : '';
    const res = await apiFetch(`/api/report/cashflow${query}`);
    return { status: res.status, data: await res.json() };
};

// Assets
export const getAssets = async () => {
    const res = await apiFetch('/api/assets');
    return { status: res.status, data: await res.json() };
};

export const getAssetById = async (id) => {
    const res = await apiFetch(`/api/assets/${id}`);
    return { status: res.status, data: await res.json() };
};

export const createAsset = async (data) => {
    const res = await apiFetch('/api/assets', { method: 'POST', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const updateAsset = async (id, data) => {
    const res = await apiFetch(`/api/assets/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const deleteAsset = async (id) => {
    const res = await apiFetch(`/api/assets/${id}`, { method: 'DELETE' });
    return { status: res.status, data: await res.text() };
};

export const getAssetReport = async (period) => {
    const params = new URLSearchParams();
    if (period) {
        params.set('period', period);
    }
    const res = await apiFetch(`/api/report/assets?${params.toString()}`);
    return { status: res.status, data: await res.json() };
};

export const downloadAssetReport = async (period, format) => {
    const params = new URLSearchParams();
    if (period) {
        params.set('period', period);
    }
    if (format) {
        params.set('format', format);
    }
    const acceptMap = {
        pdf: 'application/pdf',
        excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        csv: 'text/csv',
        xbrl: 'application/xml',
        xml: 'application/xml',
    };
    const accept = acceptMap[String(format || '').toLowerCase()] || 'application/octet-stream';
    const res = await apiFetch(`/api/report/assets?${params.toString()}`, { headers: { Accept: accept } });
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    const filename = match ? match[1] : `Anlagenspiegel.${format || 'bin'}`;
    return { status: res.status, data: blob, filename };
};

export const validateAssetReport = async (period) => {
    const params = new URLSearchParams();
    if (period) {
        params.set('period', period);
    }
    const res = await apiFetch(`/api/report/assets/validation?${params.toString()}`);
    return { status: res.status, data: await res.json() };
};

export const getDunningLevels = async () => {
    const res = await apiFetch('/api/dunning/levels');
    return { status: res.status, data: await res.json() };
};

// Recurring invoices
export const getRecurringInvoices = async () => {
    const res = await apiFetch('/api/recurring-invoice');
    return { status: res.status, data: await res.json() };
};

export const createRecurringInvoice = async (payload) => {
    const res = await apiFetch('/api/recurring-invoice', { method: 'POST', body: JSON.stringify(payload) });
    return { status: res.status, data: await res.json() };
};

export const updateRecurringInvoice = async (id, payload) => {
    const res = await apiFetch(`/api/recurring-invoice/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
    return { status: res.status, data: await res.json() };
};

export const deleteRecurringInvoice = async (id) => {
    const res = await apiFetch(`/api/recurring-invoice/${id}`, { method: 'DELETE' });
    return { status: res.status, data: await res.text() };
};

// Dashboard counts
export const getIncome = async (params = {}) => {
    const search = new URLSearchParams();
    if (params.from) {
        search.append('from', params.from);
    }
    if (params.to) {
        search.append('to', params.to);
    }
    const query = search.toString();
    const res = await apiFetch(`/api/income${query ? `?${query}` : ''}`);
    return { status: res.status, data: await res.json() };
};

export const getIncomeMonths = async () => {
    const res = await apiFetch('/api/income/months');
    return { status: res.status, data: await res.json() };
};

export const getIncomesForMonth = async (year, monthIndex) => {
    const params = new URLSearchParams();
    params.set('year', String(year));
    params.set('month', String(Number(monthIndex) + 1));
    const res = await apiFetch(`/api/income/month?${params.toString()}`);
    return { status: res.status, data: await res.json() };
};

export const getExpense = async (params = {}) => {
    const search = new URLSearchParams();
    if (params.from) {
        search.append('from', params.from);
    }
    if (params.to) {
        search.append('to', params.to);
    }
    const query = search.toString();
    const res = await apiFetch(`/api/expense${query ? `?${query}` : ''}`);
    return { status: res.status, data: await res.json() };
};

export const getExpenseMonths = async () => {
    const res = await apiFetch('/api/expense/months');
    return { status: res.status, data: await res.json() };
};

export const getExpensesForMonth = async (year, monthIndex) => {
    const params = new URLSearchParams();
    params.set('year', String(year));
    params.set('month', String(Number(monthIndex) + 1));
    const res = await apiFetch(`/api/expense/month?${params.toString()}`);
    return { status: res.status, data: await res.json() };
};

export const getExpenseById = async (id) => {
    const res = await apiFetch(`/api/expense/${id}`);
    return { status: res.status, data: await res.json() };
};

export const updateIncome = async (id, data) => {
    const res = await apiFetch(`/api/income/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const getIncomeFile = async (id) => {
    const res = await apiFetch(`/api/income/${id}/file`);
    return { status: res.status, data: await res.blob() };
};

export const uploadIncome = async (formData) => {
    const res = await apiFetch('/api/income', { method: 'POST', body: formData });
    return { status: res.status, data: await res.json() };
};

export const createIncome = async (data) => {
    const res = await apiFetch('/api/income/manual', { method: 'POST', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const parseExpense = async (formData) => {
    const res = await apiFetch('/api/expense/parse', { method: 'POST', body: formData });
    return { status: res.status, data: await res.json() };
};

export const uploadExpense = async (formData) => {
    const res = await apiFetch('/api/expense', { method: 'POST', body: formData });
    return { status: res.status, data: await res.json() };
};

export const createExpense = async (data) => {
    const res = await apiFetch('/api/expense/manual', { method: 'POST', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const updateExpense = async (id, data) => {
    const res = await apiFetch(`/api/expense/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const getExpenseReceipt = async (id) => {
    const res = await apiFetch(`/api/expense/${id}/receipt`);
    return { status: res.status, data: await res.blob() };
};

export const processExpenseReceipt = async (id, options = {}) => {
    const params = new URLSearchParams();
    if (options.force) {
        params.set('force', 'true');
    }
    const query = params.toString();
    const res = await apiFetch(`/api/expense/${id}/receipt/process${query ? `?${query}` : ''}`, {
        method: 'POST',
    });
    return { status: res.status, data: await res.json() };
};

export const uploadExpenseReceipt = async (id, formData) => {
    const res = await apiFetch(`/api/expense/${id}/receipt`, { method: 'POST', body: formData });
    return { status: res.status, data: await res.json() };
};

// Reports
export const getBwaReport = async (payload = {}) => {
    const res = await apiFetch('/api/report/bwa', { method: 'POST', body: JSON.stringify(payload) });
    return { status: res.status, data: await res.json() };
};

export const getProfitAndLossReport = async (payload = {}) => {
    const res = await apiFetch('/api/report/pnl', { method: 'POST', body: JSON.stringify(payload) });
    return { status: res.status, data: await res.json() };
};

// Recurring transactions
export const getRecurringTransactions = async (type) => {
    const res = await apiFetch(`/api/recurring?type=${encodeURIComponent(type)}`);
    return { status: res.status, data: await res.json() };
};

export const createRecurringTransaction = async (data) => {
    const res = await apiFetch('/api/recurring', { method: 'POST', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const updateRecurringTransaction = async (id, data) => {
    const res = await apiFetch(`/api/recurring/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return { status: res.status, data: await res.json() };
};

export const deleteRecurringTransaction = async (id) => {
    const res = await apiFetch(`/api/recurring/${id}`, { method: 'DELETE' });
    return { status: res.status, data: await res.text() };
};

// Evaluation
export const triggerVatAdvance = async (year, month) => {
    const res = await apiFetch(`/api/evaluation/vat/advance?year=${encodeURIComponent(year)}&month=${encodeURIComponent(month)}`, { method: 'POST' });
    return { status: res.status, data: await res.json() };
};

export const triggerVatQuarter = async (year, quarter) => {
    const quarterNumber = Number(quarter);
    if (!Number.isInteger(quarterNumber) || quarterNumber < 1 || quarterNumber > 4) {
        throw new Error('Ungültiges Quartal');
    }
    const res = await apiFetch(`/api/evaluation/vat/quarterly?year=${encodeURIComponent(year)}&quarter=${encodeURIComponent(quarterNumber)}`, {
        method: 'POST',
    });
    return { status: res.status, data: await res.json() };
};

export const triggerIncomeTax = async (yearOrOptions, options = {}) => {
    const config = typeof yearOrOptions === 'object'
        ? yearOrOptions
        : { year: yearOrOptions, ...options };
    const params = new URLSearchParams();
    params.set('year', String(config.year));
    if (config.period) {
        params.set('period', String(config.period));
    }
    if (config.month !== undefined && config.month !== null && config.month !== '') {
        params.set('month', String(config.month));
    }
    if (config.quarter !== undefined && config.quarter !== null && config.quarter !== '') {
        params.set('quarter', String(config.quarter));
    }
    const res = await apiFetch(`/api/evaluation/income-tax?${params.toString()}`, { method: 'POST' });
    return { status: res.status, data: await res.json() };
};

export const triggerVatAnnual = async (year) => {
    const res = await apiFetch(`/api/evaluation/vat/annual?year=${encodeURIComponent(year)}`, { method: 'POST' });
    return { status: res.status, data: await res.json() };
};

export const getVatEvaluationDetails = async ({ mode, year, month, quarter }) => {
    const params = new URLSearchParams();
    params.set('mode', mode);
    params.set('year', String(year));
    if (month !== undefined && month !== null && month !== '') {
        params.set('month', String(month));
    }
    if (quarter !== undefined && quarter !== null && quarter !== '') {
        params.set('quarter', String(quarter));
    }
    const res = await apiFetch(`/api/evaluation/vat/details?${params.toString()}`);
    return { status: res.status, data: await res.json() };
};

export const getIncomeTaxEvaluationDetails = async ({ year, period, month, quarter }) => {
    const params = new URLSearchParams();
    params.set('year', String(year));
    params.set('period', String(period || 'year'));
    if (month !== undefined && month !== null && month !== '') {
        params.set('month', String(month));
    }
    if (quarter !== undefined && quarter !== null && quarter !== '') {
        params.set('quarter', String(quarter));
    }
    const res = await apiFetch(`/api/evaluation/income-tax/details?${params.toString()}`);
    return { status: res.status, data: await res.json() };
};

export const downloadEvaluationPdf = async ({ mode, year, month, quarter }) => {
    const params = new URLSearchParams();
    params.set('mode', mode);
    params.set('year', String(year));
    if (month !== undefined && month !== null && month !== '') {
        params.set('month', String(month));
    }
    if (quarter !== undefined && quarter !== null && quarter !== '') {
        params.set('quarter', String(quarter));
    }
    const res = await apiFetch(`/api/evaluation/export?${params.toString()}`, {
        headers: { Accept: 'application/pdf' },
    });
    return {
        status: res.status,
        data: await res.blob(),
        filename: extractDispositionFilename(res.headers) || `steuer-auswertung-${mode}.pdf`,
    };
};

// Approvals & Audit
/** @param {{status?: string[]}} [params] */
export const getApprovalRequests = async (params = {}) => {
    const search = new URLSearchParams();
    if (Array.isArray(params.status)) {
        params.status.filter(Boolean).forEach((status) => search.append('status', status));
    }
    const query = search.toString();
    const res = await apiFetch(`/api/approvals${query ? `?${query}` : ''}`);
    return { status: res.status, data: await res.json() };
};

export const getApprovalInbox = async () => {
    const res = await apiFetch('/api/approvals/inbox');
    return { status: res.status, data: await res.json() };
};

/** @param {import('./dto/approval').ApprovalRequestCreateDTO} payload */
export const createApprovalRequest = async (payload) => {
    const res = await apiFetch('/api/approvals', { method: 'POST', body: JSON.stringify(payload) });
    return { status: res.status, data: await res.json() };
};

/** @param {string} id @param {import('./dto/approval').ApprovalDecisionDTO} [payload] */
export const approveApprovalRequest = async (id, payload) => {
    const body = payload ? JSON.stringify(payload) : undefined;
    const res = await apiFetch(`/api/approvals/${id}/approve`, { method: 'POST', body });
    return { status: res.status, data: await res.json() };
};

/** @param {string} id @param {import('./dto/approval').ApprovalDecisionDTO} [payload] */
export const rejectApprovalRequest = async (id, payload) => {
    const body = payload ? JSON.stringify(payload) : undefined;
    const res = await apiFetch(`/api/approvals/${id}/reject`, { method: 'POST', body });
    return { status: res.status, data: await res.json() };
};

/** @param {{entityType?:string,entityId?:string,action?:string,actorId?:string,from?:string,to?:string}} [params] */
export const searchAuditLog = async (params = {}) => {
    const search = new URLSearchParams();
    if (params.entityType) search.set('entityType', params.entityType);
    if (params.entityId) search.set('entityId', params.entityId);
    if (params.action) search.set('action', params.action);
    if (params.actorId) search.set('actorId', params.actorId);
    if (params.from) search.set('from', params.from);
    if (params.to) search.set('to', params.to);
    const query = search.toString();
    const res = await apiFetch(`/api/audit${query ? `?${query}` : ''}`);
    return { status: res.status, data: await res.json() };
};

// Legacy Export & Backup
export const downloadLegacyBackup = async () => {
    const res = await apiFetch('/api/export/backup', {
        headers: { Accept: 'application/zip' },
    });
    return {
        status: res.status,
        data: await res.blob(),
        filename: extractDispositionFilename(res.headers) || `schreiberlein-backup-${new Date().toISOString().slice(0, 10)}.zip`,
    };
};

